import React from 'react';
import {
    Checkbox,
    Dialog,
    DialogType,
    List,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    Stack,
    Text,
    TextField,
    TooltipHost
} from '@fluentui/react';
import {debounce} from 'lodash';
import {toast} from 'react-toastify';
import {
    ICrmImportCompany,
    useImportCrmCompanyMutation,
    useLazySearchFolioCompaniesQuery
} from '../../store/endpoints/RecordLinksEndpoints';

interface IImportCompanyDialogProps {
    /** The folio (CRM) to import from, chosen by the caller. When undefined the dialog is hidden. */
    folioId?: string;
    folioName?: string;
    onClose: () => void;
    /** Called after each successful import so the caller can refresh its company list. */
    onImported?: () => void;
}

/** One line of a CRM company candidate (city/state, identifier) for disambiguation. */
const companySubtitle = (c: ICrmImportCompany): string => {
    const place = [c.city, c.stateTerritory].filter(Boolean).join(', ');
    return [c.identifier ? `#${c.identifier}` : undefined, place || undefined].filter(Boolean).join(' · ');
};

/**
 * Search a CRM (folio) for companies and import the chosen one into Adaptive Catalog — optionally pulling all
 * of its contacts and/or locations at the same time. Each import runs synchronously against ac-api; the dialog
 * stays open so several companies can be imported in a row. Mirrors {@link CompanyLinkDialog}'s search UI.
 */
const ImportCompanyDialog: React.FC<IImportCompanyDialogProps> = (props) => {
    const [search, setSearch] = React.useState<string>('');
    const [importContacts, setImportContacts] = React.useState<boolean>(true);
    const [importLocations, setImportLocations] = React.useState<boolean>(true);
    const [importingId, setImportingId] = React.useState<string | undefined>();
    const [runSearch, {data, isFetching}] = useLazySearchFolioCompaniesQuery();
    const [importCompany] = useImportCrmCompanyMutation();

    // Reset transient state each time the dialog opens for a folio.
    React.useEffect(() => {
        if (props.folioId) {
            setSearch('');
            setImportingId(undefined);
        }
    }, [props.folioId]);

    const debouncedSearch = React.useMemo(
        () => debounce((id: string, value: string) => { void runSearch({folioId: id, search: value}); }, 400, {leading: false}),
        [runSearch]
    );

    const onSearchChange = (value?: string) => {
        setSearch(value ?? '');
        const term = (value ?? '').trim();
        if (props.folioId && term.length > 0) debouncedSearch(props.folioId, term);
    };

    const onImport = async (company: ICrmImportCompany) => {
        if (!props.folioId || !company.externalId) return;
        const label = company.name ?? company.externalId;
        setImportingId(company.externalId);
        const toastId = toast.loading(`Importing ${label}…`, {closeButton: false, closeOnClick: false, draggable: false});
        const resolved = {isLoading: false, autoClose: 4000, closeButton: true, closeOnClick: true, draggable: true};
        try {
            const result = await importCompany({
                folioId: props.folioId,
                externalId: company.externalId,
                importContacts,
                importLocations
            }).unwrap();
            if (result.success) {
                const extras = [
                    importContacts ? `${result.contacts} contacts` : undefined,
                    importLocations ? `${result.locations} locations` : undefined
                ].filter(Boolean).join(', ');
                toast.update(toastId, {type: 'success', render: `Imported ${label}${extras ? ` (${extras})` : ''}`, ...resolved});
                props.onImported?.();
            } else {
                toast.update(toastId, {type: 'error', render: result.errors?.[0] ?? `Failed to import ${label}`, ...resolved});
            }
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to import ${label}`, ...resolved});
        } finally {
            setImportingId(undefined);
        }
    };

    const companies = data?.companies ?? [];

    return (
        <Dialog
            hidden={!props.folioId}
            onDismiss={props.onClose}
            dialogContentProps={{type: DialogType.normal, title: `Import Company from ${props.folioName ?? 'CRM'}`}}
            modalProps={{isBlocking: false}}
            minWidth={520}
        >
            <Stack tokens={{childrenGap: '0.5em'}}>
                <TextField
                    label='Search by company name or identifier'
                    value={search}
                    placeholder='Company name or identifier'
                    autoFocus
                    onChange={(_e, v) => onSearchChange(v)}
                />
                <Stack horizontal tokens={{childrenGap: '1em'}}>
                    <Checkbox label='Import all contacts' checked={importContacts} onChange={(_e, c) => setImportContacts(!!c)} />
                    <Checkbox label='Import all locations' checked={importLocations} onChange={(_e, c) => setImportLocations(!!c)} />
                </Stack>
                {isFetching && <Spinner label='Searching…' />}
                {data && !data.success && <MessageBar messageBarType={MessageBarType.error}>{data.error ?? 'Search failed'}</MessageBar>}
                {!isFetching && data?.success && companies.length === 0 &&
                    <Text>No matching companies found.</Text>}
                {companies.length > 0 &&
                    <div style={{maxHeight: 340, overflowY: 'auto'}}>
                        <List
                            items={companies}
                            onRenderCell={(c?: ICrmImportCompany) => c?.externalId ? (
                                <Stack
                                    horizontal
                                    verticalAlign='center'
                                    tokens={{childrenGap: '1em'}}
                                    style={{padding: '8px 6px', borderBottom: '1px solid #edebe9'}}
                                >
                                    <Stack.Item grow>
                                        <Text block styles={{root: {fontWeight: 600}}}>{c.name ?? c.externalId}</Text>
                                        {companySubtitle(c) && <Text block variant='small'>{companySubtitle(c)}</Text>}
                                    </Stack.Item>
                                    <TooltipHost content={c.alreadyImported ? 'Company already imported' : undefined}>
                                        <PrimaryButton
                                            text={c.alreadyImported ? 'Imported' : 'Import'}
                                            iconProps={{iconName: c.alreadyImported ? 'CheckMark' : 'Download'}}
                                            disabled={c.alreadyImported || importingId !== undefined}
                                            onClick={() => onImport(c)}
                                        />
                                    </TooltipHost>
                                </Stack>
                            ) : null}
                        />
                    </div>}
            </Stack>
        </Dialog>
    );
};

export default ImportCompanyDialog;
